import { zodResolver } from '@hookform/resolvers/zod'
import { assert, NormalizedNumber, UnixDuration } from '@sparkdotfi/common-universal'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId, useConfig } from 'wagmi'

import { createTxOverview, TxOverview } from './createTxOverview'
import { validationIssueToMessage } from './validation'
import { getChainConfigEntry } from '@/config/chain'
import { formFormat } from '@/domain/common/format'
import { TokenWithBalance } from '@/domain/common/types'
import { useSnapshot } from '@/domain/hooks/useSnapshot'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { StakeSpkObjective } from '@/features/actions/flavours/stake-spk/types'
import { Objective } from '@/features/actions/logic/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import {
  getFieldsForTransferFromUserForm,
  useDebouncedFormValues,
} from '@/features/dialogs/common/logic/transfer-from-user/form'
import { getTransferFromUserFormValidator } from '@/features/dialogs/common/logic/transfer-from-user/validation'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'
import { useSpkStakingData } from '@/features/spk/staking/logic/useSpkStakingData'

export interface UseStakeDialogParams {
  minStake?: NormalizedNumber
  prefillWithMax?: boolean
  onSuccess?: (spkStaked: NormalizedNumber) => void
}

export interface UseStakeDialogResult {
  selectableAssets: TokenWithBalance[]
  assetsFields: FormFieldsForDialog
  form: UseFormReturn<AssetInputSchema>
  objectives: Objective[]
  actionsViewState: ActionsViewState
  spk: Token
  staked: NormalizedNumber
  pageStatus: PageStatus
  txOverview: TxOverview
  epochDuration: UnixDuration
}

export function useStakeDialog({ prefillWithMax, onSuccess }: UseStakeDialogParams): UseStakeDialogResult {
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const chainId = useChainId()
  const { account } = useConnectedAddress()
  const wagmiConfig = useConfig()

  const sparkTokenFeatureConfig = getChainConfigEntry(chainId).spk
  assert(sparkTokenFeatureConfig, 'Spark token config should be defined')

  const tokenRepository = useSpkTokenRepository({
    chainId,
    account,
  })
  const spk = tokenRepository.findOneBySymbol(TokenSymbol('SPK'))

  const { spkStakingData } = useSpkStakingData({
    chainId,
    account,
    wagmiConfig,
    tokenRepository,
  })

  const spkBalance = tokenRepository.findOneBalance(spk.symbol)

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(
      getTransferFromUserFormValidator({
        tokenRepository,
        issueToMessage: validationIssueToMessage,
      }),
    ),
    defaultValues: {
      symbol: spk.symbol,
      value: prefillWithMax ? formFormat(spkBalance, spk.decimals) : '',
      isMaxSelected: prefillWithMax,
    },
    mode: 'onChange',
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
  useEffect(() => {
    // @note: if prefillWithMax is set and user has not touched any field, form was initialized with
    // with max value, so we need to update it if the balance changes.
    // We use Object.keys(form.formState.dirtyFields).length === 0 because form.formState.isDirty is not
    // also set to true when form.setValue is called, but form.formState.dirtyFields reacts only to user input.
    if (prefillWithMax && Object.keys(form.formState.dirtyFields).length === 0) {
      form.setValue('isMaxSelected', true)
      form.setValue('value', formFormat(spkBalance, spk.decimals))
      // biome-ignore lint/suspicious/noConsole: debugging
      form.trigger().catch(console.error)
    }
  }, [form, prefillWithMax, spkBalance.toString()])

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedFormValues({
    form,
    tokenRepository,
  })

  const objectives: StakeSpkObjective[] = [
    {
      type: 'stakeSpk',
      spk,
      amount: formValues.value,
    },
  ]

  const txOverview = createTxOverview({
    nextEpochEnd: spkStakingData.nextEpochEnd,
    timestamp: spkStakingData.timestamp,
    formValues,
  })

  const actionsEnabled = formValues.value.gt(0) && isFormValid && !isDebouncing

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      chainId,
    },
    onFinish: () => {
      if (onSuccess) {
        onSuccess(formValues.value)
      } else {
        setPageStatus('success')
      }
    },
    enabled: actionsEnabled,
  })

  const [staked, refreshStaked] = useSnapshot(formValues.value)

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
    refreshStaked()
  }, [formValues.token.symbol, formValues.value.toString(), formValues.isMaxSelected, resetActions, refreshStaked])

  return {
    spk,
    staked,
    selectableAssets: [{ token: spk, balance: spkBalance }],
    assetsFields: getFieldsForTransferFromUserForm({ form, tokenRepository }),
    form,
    objectives,
    actionsViewState,
    txOverview,
    epochDuration: spkStakingData.epochDuration,
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      goToSuccessScreen: () => {
        if (onSuccess) {
          onSuccess(formValues.value)
        } else {
          setPageStatus('success')
        }
      },
    },
  }
}
